import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { isFunction } from 'lodash-es';
import type { ChangeEvent } from 'react';
import { useCallback } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import { createFilterDefHelper, useComposableFilter } from '@/components/ComposableFilter';
import { PlaceFilterChip, SoftwareFilterChip } from '@/components/FilterChip';
import { HStack } from '@/components/Layout';
import FillScreen from '@/components/Layout/FillScreen';
import SearchInput from '@/components/SearchInput';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { SoftwarePeopleTable } from './-components/AppPeople';
import { WorkForceMegaMenu } from './-components/WorkForceMegaMenu';
import { useSoftwarePeopleExport } from './-query';
import type { SearchSchema } from './-schemas';
import type { SoftwarePeopleFilter } from './-types';
export const Route = createFileRoute('/_staff/software/_layout/people/')({
  component: People
});
const helper = createFilterDefHelper<SoftwarePeopleFilter>();
const filterDefs = [helper('softwareIds', {
  component: SoftwareFilterChip
}),
// helper("workerTypeIds", { component: WorkerTypeFilterChip }),
helper('placeIds', {
  component: PlaceFilterChip
})];
function People() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.software.people'
  });
  const {
    t: tCommon
  } = useTranslation(defaultNS, {
    keyPrefix: 'common'
  });
  const navigate = useNavigate({
    from: '/software/people'
  });
  const search = Route.useSearch();
  const {
    mutate: exportAppUsersCsv,
    isPending
  } = useSoftwarePeopleExport({
    freeText: search.freeText,
    softwareIdIn: search.softwareIds,
    placeIdIn: search.placeIds,
    category: search.category
  });
  const filter = useComposableFilter<SoftwarePeopleFilter>({
    filterDefs,
    filterState: search,
    onFilterStateChange: useCallback(state => navigate({
      search: (prev: SearchSchema) => ({
        ...prev,
        ...(isFunction(state) ? state(prev) : state)
      })
    }), [navigate])
  });
  const handleFilterInput = useDebouncedCallback((e: ChangeEvent<HTMLInputElement>) => navigate({
    search: (prev: SearchSchema) => ({
      ...prev,
      freeText: e.target.value,
      pageIndex: 0
    })
  }), [navigate]);
  return <FillScreen className="flex flex-col gap-4 px-12 py-5">
      <WorkForceMegaMenu />
      <HStack className="justify-between items-center p-2 rounded bg-background">
        <HStack className="gap-2">
          <SearchInput className="h-8 w-interaction-box-sm" placeholder={t('filterPeoplePlaceholder')} defaultValue={search.freeText} onChange={handleFilterInput} />
          {filter.getAllFilterDefs().map(({
          component: Component,
          ...def
        }) => <Component key={def.id} value={filter.filterState[def.id]} onChange={filter.getOnChangeHandler(def.id)} />)}
        </HStack>
        <ActionButton icon="Download" variant="ghost" iconColor="primary" onClick={() => exportAppUsersCsv()} isLoading={isPending}>
          {tCommon('action.export')}
        </ActionButton>
      </HStack>
      <SoftwarePeopleTable />
    </FillScreen>;
}